import React, {useId} from 'react';

export const OUTPUT_TYPES = [
  {value: 'address', label: 'Address labels'},
  {value: 'packing', label: 'Packing labels'},
  {value: 'return', label: 'Return labels'},
  {value: 'slip', label: 'Packing slips (full page)'},
];

export const RETURN_ADDRESS_MAX = 500;

/**
 * Controlled options form. `values` is { output, template, skip, returnAddress, remember };
 * `onChange(patch)` receives a partial update. `templates` is the registry from the settings API.
 */
export default function LabelOptionsForm({values, templates, onChange, disabled = false}) {
  // Page and modal variants can be mounted together, so ids and the radio group name are per instance.
  const uid = useId();
  const template = templates[values.template];
  const isSlip = values.output === 'slip';
  const maxSkip = template ? Math.max(0, template.perPage - 1) : 0;

  const setSkip = event => {
    const parsed = parseInt(event.target.value, 10);
    const next = Number.isNaN(parsed) ? 0 : Math.min(Math.max(parsed, 0), maxSkip);
    onChange({skip: next});
  };

  return (
    <div className="label-options-form">
      <fieldset disabled={disabled}>
        <legend>What to print</legend>
        {OUTPUT_TYPES.map(type => (
          <label key={type.value} className="radio" htmlFor={`${uid}-output-${type.value}`}>
            <input
              type="radio"
              id={`${uid}-output-${type.value}`}
              name={`${uid}-output`}
              value={type.value}
              checked={values.output === type.value}
              onChange={() => onChange({output: type.value})}
            />
            <span>{type.label}</span>
          </label>
        ))}
      </fieldset>

      {!isSlip && (
        <div className="label-options-form__field">
          <label htmlFor={`${uid}-template`}>Label stock</label>
          <select
            id={`${uid}-template`}
            value={values.template}
            disabled={disabled}
            onChange={event => onChange({template: event.target.value, skip: 0})}
          >
            {Object.entries(templates).map(([key, entry]) => (
              <option key={key} value={key}>
                {key} ({entry.name}, {entry.perPage} per sheet)
              </option>
            ))}
          </select>
        </div>
      )}

      {!isSlip && (
        <div className="label-options-form__field">
          <label htmlFor={`${uid}-skip`}>Labels already used on the first sheet</label>
          <input
            type="number"
            id={`${uid}-skip`}
            min="0"
            max={maxSkip}
            value={values.skip}
            disabled={disabled || maxSkip === 0}
            onChange={setSkip}
          />
        </div>
      )}

      {values.output === 'return' && (
        <div className="label-options-form__field">
          <label htmlFor={`${uid}-return-address`}>Return address</label>
          <textarea
            id={`${uid}-return-address`}
            rows="4"
            maxLength={RETURN_ADDRESS_MAX}
            value={values.returnAddress}
            disabled={disabled}
            onChange={event => onChange({returnAddress: event.target.value})}
          />
          <label className="checkbox" htmlFor={`${uid}-remember-address`}>
            <input
              type="checkbox"
              id={`${uid}-remember-address`}
              checked={values.remember}
              disabled={disabled}
              onChange={event => onChange({remember: event.target.checked})}
            />
            <span>Remember this return address</span>
          </label>
        </div>
      )}
    </div>
  );
}
